import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, PackagePlus, SearchX } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, LogoMark, SearchField, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import { Thumb } from '../../../components/design-system/analytics/Thumb';
import { SelectionBar } from '../../../components/design-system/common';
import { FilterChip } from '../../../components/design-system/common/FilterChip';
import { PageContainer } from '../../../common/components/PageContainer';
import {
    BrandProduct,
    BrandProductFilters,
    NetworkAllowance,
    NetworkBrandDetail,
    Paged,
    useNetworkApi,
} from '../../../common/hooks/use-network-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { ImportDialog } from '../../../modules/vendor/network/ImportDialog';
import { countryLabel, formatLabel, vocabLabel } from '../../../modules/vendor/network/network-labels';

const PAGE_SIZE = 24;

/**
 * One brand's products in the network: its collections, filters, and a grid
 * to pick from — what the shop already has marked, the rest selectable —
 * ending in "Add to my catalogue".
 */
export const NetworkBrandPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { slug = '' } = useParams<{ slug: string }>();
    const api = useNetworkApi();

    const [detail, setDetail] = useState<NetworkBrandDetail | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [filters, setFilters] = useState<BrandProductFilters>({});
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [products, setProducts] = useState<Paged<BrandProduct> | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [allowance, setAllowance] = useState<NetworkAllowance | null>(null);
    const [dialog, setDialog] = useState<{ open: boolean; uuids: string[] }>({ open: false, uuids: [] });
    const [collecting, setCollecting] = useState<string | null>(null);

    const loadDetail = useCallback(async () => {
        try {
            setDetail(await api.brand(slug));
        } catch (e) {
            setDetailError((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        }
    }, [api, slug, t]);

    const loadAllowance = useCallback(async () => {
        try {
            setAllowance((await api.summary()).allowance);
        } catch {
            setAllowance(null);
        }
    }, [api]);

    useEffect(() => {
        loadDetail();
        loadAllowance();
    }, [loadDetail, loadAllowance]);

    // The search box waits for the typing to stop.
    useEffect(() => {
        const id = window.setTimeout(() => {
            const q = search.trim() || undefined;
            // Unchanged (the first render too): keep the same object, so nothing reloads.
            setFilters((f) => {
                if (f.q === q) return f;
                setPage(1); // idempotent, so a double-invoked updater is harmless
                return { ...f, q };
            });
        }, 300);
        return () => window.clearTimeout(id);
    }, [search]);

    const loadProducts = useCallback(async () => {
        setLoading(true);
        try {
            setProducts(await api.brandProducts(slug, { ...filters, page, limit: PAGE_SIZE }));
        } catch (e) {
            toast.error((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setLoading(false);
        }
    }, [api, slug, filters, page, t]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const setFilter = (key: keyof BrandProductFilters, value?: string) => {
        setFilters((f) => ({ ...f, [key]: value || undefined }));
        setPage(1);
    };

    const items = products?.items || [];
    const pickable = items.filter((p) => !p.inMyCatalog);
    const allPicked = pickable.length > 0 && pickable.every((p) => selected.has(p.uuid));
    const somePicked = pickable.some((p) => selected.has(p.uuid));

    const addCollection = async (collection: string) => {
        setCollecting(collection);
        try {
            const { uuids } = await api.brandProductUuids(slug, { collection });
            if (!uuids.length) {
                toast.success(t('NETWORK_RETAILER.BRAND.COLLECTION_ALL_IN'));
                return;
            }
            setDialog({ open: true, uuids });
        } catch (e) {
            toast.error((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setCollecting(null);
        }
    };

    const brand = detail?.brand;
    const facets = detail?.facets;
    const crumbs = useMemo(
        () => [{ label: t('SIDEBAR.PRODUCTS.TITLE') }, { label: t('NETWORK_RETAILER.NAV'), href: '/products/network?tab=browse' }, { label: brand?.name || '…' }],
        [t, brand?.name],
    );

    if (detailError) {
        return (
            <PageContainer breadcrumbs={crumbs}>
                <div className="mx-auto max-w-[1100px] rounded-xl border bg-card" data-testid="network-brand-error">
                    <EmptyState
                        icon={<SearchX />}
                        title={detailError}
                        action={
                            <Button variant="outline" asChild>
                                <Link to="/products/network?tab=browse">{t('NETWORK_RETAILER.BRAND.BACK')}</Link>
                            </Button>
                        }
                    />
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer breadcrumbs={crumbs}>
            <div className="mx-auto flex max-w-[1100px] flex-col gap-6" data-testid="network-brand-page">
                <Link to="/products/network?tab=browse" className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden />
                    {t('NETWORK_RETAILER.BRAND.BACK')}
                </Link>

                {/* The brand */}
                {!detail ? (
                    <Skeleton className="h-28 rounded-xl" />
                ) : (
                    <section className="flex flex-col gap-4 rounded-xl border bg-card p-6 md:flex-row md:items-start md:justify-between" data-testid="network-brand-header">
                        <div className="flex items-start gap-4">
                            <LogoMark src={brand!.logoUrl} name={brand!.name} className="size-14" />
                            <div className="max-w-2xl">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h1 className="font-heading text-[22px] font-medium tracking-tight">{brand!.name}</h1>
                                    {brand!.carried && <StatusBadge tone="neutral">{t('NETWORK_RETAILER.BROWSE.YOU_SELL')}</StatusBadge>}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {[countryLabel(brand!.country, i18n.language), brand!.materials.map((m) => vocabLabel(t, m)).join(', ')].filter(Boolean).join(' · ')}
                                </p>
                                {brand!.description && <p className="mt-2 text-sm text-muted-foreground">{brand!.description}</p>}
                            </div>
                        </div>
                        <dl className="grid shrink-0 grid-cols-3 gap-4 md:text-end">
                            <HeaderFact label={t('NETWORK_RETAILER.BROWSE.COLLECTIONS')} value={formatNumber(detail.collections.length)} />
                            <HeaderFact label={t('NETWORK_RETAILER.BROWSE.PRODUCTS')} value={formatNumber(detail.totals.products)} />
                            <HeaderFact label={t('NETWORK_RETAILER.BROWSE.IN_CATALOGUE')} value={formatNumber(detail.totals.inMyCatalog)} />
                        </dl>
                    </section>
                )}

                {/* Collections */}
                {detail && detail.collections.length > 0 && (
                    <section className="flex flex-col gap-3">
                        <h2 className="text-sm font-medium">{t('NETWORK_RETAILER.BRAND.COLLECTIONS')}</h2>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="network-collections">
                            {detail.collections.map((c) => {
                                const active = filters.collection === c.slug;
                                const all = c.inMyCatalog >= c.products;
                                return (
                                    <div key={c.slug} className={cn('flex gap-3 rounded-xl border bg-card p-3', active && 'border-foreground/40')} data-testid="network-collection">
                                        <button type="button" onClick={() => setFilter('collection', active ? undefined : c.slug)} className="shrink-0" aria-pressed={active}>
                                            <Thumb src={c.coverImageUrl} alt={c.name} size="fill" className="size-16 rounded-lg" />
                                        </button>
                                        <div className="min-w-0 flex-1">
                                            <button type="button" onClick={() => setFilter('collection', active ? undefined : c.slug)} className="block max-w-full truncate text-start text-sm font-medium hover:underline">
                                                {c.name}
                                            </button>
                                            <p className="truncate text-xs text-muted-foreground">
                                                {[vocabLabel(t, c.appearance), vocabLabel(t, c.material)].filter(Boolean).join(' · ')}
                                            </p>
                                            <p className="text-xs text-muted-foreground tabular-nums">
                                                {t('NETWORK_RETAILER.BRAND.COLLECTION_COUNTS', { products: formatNumber(c.products), mine: formatNumber(c.inMyCatalog) })}
                                            </p>
                                            <Button
                                                variant="outline"
                                                size="xs"
                                                className="mt-2"
                                                disabled={all || collecting !== null}
                                                onClick={() => addCollection(c.slug)}
                                                data-testid="network-add-collection"
                                            >
                                                {collecting === c.slug ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <PackagePlus data-icon="inline-start" />}
                                                {all ? t('NETWORK_RETAILER.BRAND.COLLECTION_ALL_IN_SHORT') : t('NETWORK_RETAILER.BRAND.ADD_COLLECTION')}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Filters */}
                <section className="flex flex-col gap-3" data-testid="network-brand-filters">
                    <div className="flex flex-wrap items-center gap-2">
                        <SearchField value={search} onChange={setSearch} placeholder={t('NETWORK_RETAILER.BRAND.SEARCH')} />
                        <FacetSelect label={t('NETWORK_RETAILER.BRAND.MATERIAL')} value={filters.material} options={facets?.materials.map((f) => ({ value: f.value, label: vocabLabel(t, f.value) }))} onChange={(v) => setFilter('material', v)} />
                        <FacetSelect label={t('NETWORK_RETAILER.BRAND.LOOK')} value={filters.appearance} options={facets?.appearances.map((f) => ({ value: f.value, label: vocabLabel(t, f.value) }))} onChange={(v) => setFilter('appearance', v)} />
                        <FacetSelect label={t('NETWORK_RETAILER.BRAND.COLOUR')} value={filters.colorFamily} options={facets?.colorFamilies.map((f) => ({ value: f.value, label: vocabLabel(t, f.value) }))} onChange={(v) => setFilter('colorFamily', v)} />
                        <FacetSelect label={t('NETWORK_RETAILER.BRAND.FORMAT')} value={filters.format} options={facets?.formats.map((f) => ({ value: f.key, label: formatLabel(f) }))} onChange={(v) => setFilter('format', v)} />
                        <FilterChip
                            size="md"
                            label={t('NETWORK_RETAILER.BRAND.NOT_IN_CATALOGUE')}
                            selected={filters.inCatalog === 'no'}
                            onClick={() => setFilter('inCatalog', filters.inCatalog === 'no' ? undefined : 'no')}
                        />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                        <span className="tabular-nums">{products ? t('NETWORK_RETAILER.BRAND.SHOWING', { count: products.total }) : ''}</span>
                        {pickable.length > 0 && (
                            <label className="flex cursor-pointer items-center gap-2">
                                <Checkbox
                                    checked={allPicked ? true : somePicked ? 'indeterminate' : false}
                                    onCheckedChange={(v) =>
                                        setSelected((s) => {
                                            const next = new Set(s);
                                            pickable.forEach((p) => (v === true ? next.add(p.uuid) : next.delete(p.uuid)));
                                            return next;
                                        })
                                    }
                                    data-testid="network-select-page"
                                />
                                {t('NETWORK_RETAILER.BRAND.SELECT_PAGE')}
                            </label>
                        )}
                    </div>
                </section>

                {/* Products */}
                {loading && !products ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} className="aspect-[3/4] rounded-xl" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-xl border bg-card" data-testid="network-products-empty">
                        <EmptyState icon={<SearchX />} title={t('NETWORK_RETAILER.BRAND.NO_PRODUCTS')} description={t('NETWORK_RETAILER.BRAND.NO_PRODUCTS_HINT')} />
                    </div>
                ) : (
                    <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4', loading && 'opacity-60')} data-testid="network-products">
                        {items.map((p) => (
                            <ProductTile
                                key={p.uuid}
                                product={p}
                                selected={selected.has(p.uuid)}
                                onToggle={(v) =>
                                    setSelected((s) => {
                                        const next = new Set(s);
                                        if (v) next.add(p.uuid);
                                        else next.delete(p.uuid);
                                        return next;
                                    })
                                }
                            />
                        ))}
                    </div>
                )}

                {products && products.total > PAGE_SIZE && (
                    <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                        <span className="tabular-nums">
                            {t('NETWORK_RETAILER.PAGINATION', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, products.total), total: products.total })}
                        </span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((n) => n - 1)}>
                                {t('NETWORK_RETAILER.PREVIOUS')}
                            </Button>
                            <Button variant="outline" size="sm" disabled={!products.nextPage || loading} onClick={() => setPage((n) => n + 1)}>
                                {t('NETWORK_RETAILER.NEXT')}
                            </Button>
                        </div>
                    </div>
                )}

                <SelectionBar open={selected.size > 0} label={t('NETWORK_RETAILER.SELECTED', { count: selected.size })} testId="network-brand-bar">
                    <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>
                        {t('NETWORK_RETAILER.CLEAR_SELECTION')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDialog({ open: true, uuids: [...selected] })} data-testid="network-add-selected">
                        <PackagePlus data-icon="inline-start" />
                        {t('NETWORK_RETAILER.BRAND.ADD_SELECTED')}
                    </Button>
                </SelectionBar>
            </div>

            <ImportDialog
                open={dialog.open}
                onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
                uuids={dialog.uuids}
                brandName={brand?.name}
                allowance={allowance}
                onImported={() => {
                    setSelected(new Set());
                    loadProducts();
                    loadDetail();
                    loadAllowance();
                }}
            />
        </PageContainer>
    );
};

/** One network product: picture, name, format; ticked to add, or marked as already in the catalogue. */
const ProductTile: React.FC<{ product: BrandProduct; selected: boolean; onToggle: (v: boolean) => void }> = ({ product, selected, onToggle }) => {
    const { t } = useTranslation();
    const p = product;
    return (
        <div className={cn('relative flex flex-col overflow-hidden rounded-xl border bg-card', selected && 'border-foreground/40')} data-testid="network-product" data-in-catalogue={p.inMyCatalog || undefined}>
            <label className={cn('block', !p.inMyCatalog && 'cursor-pointer')}>
                <Thumb src={p.image || p.textureUrl} alt={p.name} size="fill" className="aspect-square w-full rounded-none border-0 border-b" />
                {!p.inMyCatalog && (
                    <span className="absolute start-2 top-2 rounded-md bg-card/90 p-1">
                        <Checkbox checked={selected} onCheckedChange={(v) => onToggle(v === true)} aria-label={t('NETWORK_RETAILER.BRAND.SELECT_ONE', { name: p.name })} />
                    </span>
                )}
            </label>
            <div className="flex flex-1 flex-col gap-0.5 p-3">
                <p className="truncate text-sm font-medium" title={p.name}>
                    {p.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">{[p.collection?.name, formatLabel(p.format)].filter(Boolean).join(' · ')}</p>
                <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                    {p.colour?.hex && <span className="size-2.5 shrink-0 rounded-full border" style={{ backgroundColor: p.colour.hex }} aria-hidden />}
                    {[p.colour?.name, vocabLabel(t, p.finish)].filter(Boolean).join(' · ')}
                </p>
                {/* How many other shops sell it is deliberately not shown: a count of one or two names a competitor. */}
                {p.inMyCatalog && (
                    <div className="mt-auto pt-2">
                        {p.myProduct?.uuid ? (
                            <Link to={`/products/${p.myProduct.uuid}/edit`} className="hover:underline" data-testid="network-in-catalogue">
                                <StatusBadge tone="success">{t('NETWORK_RETAILER.BRAND.IN_CATALOGUE')}</StatusBadge>
                            </Link>
                        ) : (
                            <StatusBadge tone="success">{t('NETWORK_RETAILER.BRAND.IN_CATALOGUE')}</StatusBadge>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const FacetSelect: React.FC<{
    label: string;
    value?: string;
    options?: Array<{ value: string; label: string }>;
    onChange: (v?: string) => void;
}> = ({ label, value, options, onChange }) => {
    const { t } = useTranslation();
    if (!options || options.length < 2) return null;
    return (
        <NativeSelect value={value || ''} onChange={(e) => onChange(e.target.value || undefined)} aria-label={label} className="h-9">
            <option value="">{t('NETWORK_RETAILER.BRAND.ANY', { label })}</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </NativeSelect>
    );
};

const HeaderFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="text-lg font-medium tabular-nums">{value}</dd>
    </div>
);

export default NetworkBrandPage;
